import { Component, Host, Prop, State, h } from '@stencil/core';
import { Article, Category, Tag } from '../../types';

declare global {
  interface Window {
    getTranslation: any;
  }
}

@Component({
  tag: 'kwo-articles-grid',
  styleUrl: 'kwo-articles-grid.scss',
  shadow: false,
})
export class KwoArticlesGrid {
  @Prop() articles: string;
  @Prop() showFilter: string;
  @Prop() loadMoreItems: number = 6;
  private addItems: number = this.loadMoreItems;

  @State() articlesArray: Article[];
  @State() showFilterBool: boolean;
  @State() showTagsBool: boolean = false;

  @State() filteredArticles: Article[] = [];
  @State() selectedCategory: number | null = null;
  @State() selectedTag: number | null = null;
  @State() categories: Category[] = [];
  @State() tags: Tag[] = [];
  private featuredArticle: Article;

  componentDidLoad() {
    this.articlesArray = JSON.parse(this.articles);
    this.showFilterBool = JSON.parse(this.showFilter);
    this.filteredArticles = [...this.articlesArray];
    this.featuredArticle = this.filteredArticles.find(article => article.featured === true);
    this.categories = [...new Map(this.articlesArray.map(item => [item.category.id, item.category])).values()].sort((a, b) => a.orderIndex - b.orderIndex);
    this.tags = [...new Map(this.articlesArray.flatMap(item => item.tags).map(tag => [tag.id, tag])).values()].sort((a, b) => a.orderIndex - b.orderIndex);

    const urlParams = new URLSearchParams(window.location.search);
    const tagParam = urlParams.get('tag');
    if (tagParam) { this.filterBy('tag', parseInt(tagParam, 10)) };
  }

  filterItems() {
    this.filteredArticles = this.articlesArray
      .filter(item => {
        const matchesCategory = this.selectedCategory === null || item.category.id === this.selectedCategory;
        const matchesTag = this.selectedTag === null || item.tags.some(tag => tag.id === this.selectedTag);
        return matchesCategory && matchesTag;
      });
  }

  filterBy(type: 'category' | 'tag', id: number) {
    if (type === 'category') {
      this.selectedCategory = id;
    }
    else if (type === 'tag') {
      this.selectedTag = this.selectedTag === id ? null : id;
    }
    this.filterItems();
    this.featuredArticle = this.selectedTag || this.selectedCategory ? null : this.filteredArticles.find(article => article.featured === true);
    this.showTagsBool = false;
  }

  showTags() {
    this.showTagsBool = !this.showTagsBool;
  }

  resetFilters() {
    this.selectedCategory = null;
    this.selectedTag = null;
    this.filterItems();
    this.featuredArticle = this.filteredArticles.find(article => article.featured === true) || null;
    this.showTagsBool = false;
  }

  loadMore() {
    this.loadMoreItems += this.addItems;
  }

  render() {
    return (
      <Host>
        <div class="row">
          {this.showFilterBool && this.categories.length > 1 ? (
            <div class="col-12 position-relative border-top border-bottom border-primary py-md mb-xl">
              <div class="row">
                <div class="col-12 col-xl-10 offset-xl-2">
                  <div class="row">
                    <div class="categories col-12 col-md-8 d-flex flex-wrap gap-lg gap-xl-xl mb-lg mb-md-0">
                      <button class={`btn btn-link text-uppercase ${!this.selectedCategory ? 'active' : ''}`} onClick={() => this.filterBy('category', null)}>
                        {window.getTranslation('all')}
                      </button>
                      {this.categories.map(category => (
                        <button class={`btn btn-link text-uppercase ${this.selectedCategory == category.id ? 'active' : ''}`} onClick={() => this.filterBy('category', category.id)}>
                          {category.title}
                        </button>
                      ))}
                    </div>
                    <div class="filter col-12 col-md-4 d-flex flex-wrap justify-content-md-end gap-md">
                      <button class="btn btn-link" onClick={() => this.showTags()}>
                        <lg-icon name="filter" class="me-2xs"></lg-icon>{window.getTranslation('tag-filter')}
                      </button>
                      {this.selectedCategory || this.selectedTag ? (
                        <button class="btn btn-link" onClick={() => this.resetFilters()}>
                          <lg-icon name="close"></lg-icon>{window.getTranslation('reset_filters')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <div class={`tags bg-white z-3 ${this.showTagsBool && 'show'}`}>
                <div class="row">
                  <div class="col-12 col-xl-10 offset-xl-2 d-flex flex-wrap align-items-center gap-md py-lg">
                    {this.tags.map(tag => (
                      <button class={`btn btn-tags ${this.selectedTag == tag.id ? 'active' : ''}`} onClick={() => this.filterBy('tag', tag.id)}>
                        {tag.title}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {this.featuredArticle && (
            <div class="col-12 mb-2xl">
                {this.filteredArticles.length > 0 && (
                  <a class="Teaser__link" href={ this.featuredArticle.url }>
                    <div class="Teaser Teaser-big row position-relative pt-md pt-lg-0">
                      <div class="Teaser-big__image col-12 mb-lg position-relative">
                        <picture>
                          <source media="(min-width: 768px)" srcSet={this.featuredArticle.image.url16x7} />
                          <img src={this.featuredArticle.image.url} alt={this.featuredArticle.image.alt} class="img-fluid" />
                        </picture>
                        { this.featuredArticle.category.title && (
                          <div class="position-absolute top-0 p-md">
                            <span class="Badge Badge--primary">{this.featuredArticle.category.title}</span>
                          </div>
                        )}
                      </div>

                      <div class="col-12 col-lg-5 col-xl-4 offset-lg-1 offset-xl-2">
                        <h2 class="h3" innerHTML={ this.featuredArticle.title }></h2>
                      </div>

                      <div class="col-12 col-lg-6 px-lg-lg">
                        <div innerHTML={this.featuredArticle.text}></div>
                        <div class="my-lg">
                          <span class="btn btn-tertiary">
                            {window.getTranslation('read-more')}<lg-icon name="arrow-up-right-thin" class="ms-sm"></lg-icon>
                          </span>
                        </div>
                      </div>
                      <div class="mt-md"><hr class="m-0" /></div>
                    </div>
                  </a>
                )}
            </div>
          )}

          <div class="col-12 col-xl-10 offset-xl-2">
            <div class="row">
              { this.filteredArticles.length > 0 ? (
                this.filteredArticles
                  .filter(article => this.featuredArticle ? article.id !== this.featuredArticle?.id : true)
                  .slice(0, this.loadMoreItems)
                  .map(article => (
                  <div class="TeaserBox__article col-12 col-md-4 mb-lg">
                    <kwo-article-card article={article}></kwo-article-card>
                  </div>
                ))
              ) : window.getTranslation('no_result') }
            </div>
          </div>

          {this.loadMoreItems && this.loadMoreItems < (this.filteredArticles.length - (this.featuredArticle ? 1 : 0)) ? (
          <div class="col-12 col-xl-10 offset-xl-2 text-center mt-xl">
            <button class="btn btn-primary" onClick={() => this.loadMore()}>
              {window.getTranslation('load-more')}
            </button>
          </div>
          ) : null}

        </div>
      </Host>
    );
  }
}
